import React, { Fragment, useContext, useEffect, useReducer, useState } from 'react';
import Screen from 'utils/helpers/ScreenSize';
import { Dialog, Transition } from '@headlessui/react';
import { StoreContext } from 'pages/_app';
import { toJS } from 'mobx';
import { observer } from 'mobx-react';
import { useRouter } from 'next/router';
import classNames from 'classnames';
import QueryString from 'qs';
import { formattedAddres, parsedAddress } from 'utils/helpers/addresses';
import AddressInput from 'components/Shared/AddressInput';
import Image from 'next/image';
import { MapIcon, ArrowRightIcon } from 'assets/img';

function DuplicateAddressConfirmation({ visible, setModal, addresses }) {
  const screenType = Screen.ScreenType();
  const store = useContext(StoreContext);
  const cartStore = toJS(store?.cartStore);
  let { address, selectedDate, selectedTime, activeEmployees } = store?.userStore;

  let router = useRouter();

  const initialState = {
    email: '',
    password: '',
    showPassword: false,
    error: '',
  };

  function reducer(state, action) {
    switch (action.type) {
      case 'reset':
        return initialState;
    }
    return {
      ...state,
      [action.field]: action.value,
    };
  }

  const [provider, dispatch] = useReducer(reducer, initialState);

  let { showPassword, email, password, error } = provider;

  const [enableContinue, setEnableContinue] = useState(true);

  const [isNewAddressSelected, setIsNewAddressSelected] = useState(false);

  const [selectedAddressId, setSelectedAddressId] = useState(null);

  const [enteredAddress, setEnteredAddress] = useState('');
  const [typedCompleteAddress, setTypedCompleteAddress] = useState(null);

  useEffect(() => {
    if (visible && Array.isArray(addresses) && addresses.length > 0) {
      // Preserve the originally typed address (full line, no nickname)
      const typedComplete = toJS(store?.userStore?.completeAddress);
      const typedFull = typedComplete
        ? buildFullAddress(typedComplete)
        : store?.userStore?.address || '';
      setEnteredAddress(typedFull);
      setTypedCompleteAddress(typedComplete);

      setSelectedAddressId(addresses[0].id);
      setIsNewAddressSelected(false);
      setEnableContinue(false);
      const first = addresses[0];
      const formatted = formattedAddres(first.attributes);
      const complete = parsedAddress(first.attributes);
      store.userStore?.setAddressID(first.id);
      store.userStore?.setAddressLocationID(first.locationId);
      store.userStore?.setAddress(formatted, complete);
    }
  }, [visible, addresses]);

  const onContinuePress = e => {
    e.preventDefault();

    if (router.pathname !== '/checkout/[slug]')
      router.replace(
        {
          pathname: router?.pathname,
          query: {
            ...QueryString.parse(router?.query),
            address: address,
          },
        },
        undefined,
        {
          shallow: true,
        }
      );

    setModal(false);
    setTimeout(() => {
      document.documentElement.removeAttribute('style');
    }, 0);
  };

  const closeModal = () => {
    setModal(false);
    setTimeout(() => {
      document.documentElement.removeAttribute('style');
    }, 0);
  };

  const setInvoiceDetailsForLocation = locationId => {
    const account = activeEmployees?.find(emp => emp?.attributes?.organization_id == locationId);
    const isInvoiceEnabled = account?.attributes?.invoice_payments == 'enabled';

    if (isInvoiceEnabled)
      store.userStore.setInvoiceDetails({
        invoice_payments: account?.attributes?.invoice_payments,
        account_billing_address: account?.attributes?.account_billing_address,
        account_billing_emails: account?.attributes?.account_billing_emails,
        account_billing_name: account?.attributes?.account_billing_name,
      });
    else
      store.userStore.setInvoiceDetails({
        invoice_payments: '',
        account_billing_address: '',
        account_billing_emails: '',
        account_billing_name: '',
      });
  };

  const selectExistingAddress = adress => {
    const formattedAddress = formattedAddres(adress?.attributes);
    const completeAddress = parsedAddress(adress?.attributes);

    setIsNewAddressSelected(false);
    setSelectedAddressId(adress.id);

    store.userStore?.setAddressID(adress.id);
    store.userStore?.setAddressLocationID(adress?.locationId);
    store?.userStore?.setAddress(formattedAddress, completeAddress);

    setInvoiceDetailsForLocation(adress?.locationId);
    setEnableContinue(false);

    // Update URL and close immediately on mobile selection
    router.replace(
      {
        pathname: router?.pathname,
        query: {
          ...QueryString.parse(router?.query),
          address: formattedAddress,
        },
      },
      undefined,
      { shallow: true }
    );
    closeModal();
  };

  const selectNewAddress = () => {
    setIsNewAddressSelected(true);
    setSelectedAddressId(null);
    store.userStore?.setIsNewAddress(true);
    // Set store to the typed address (no nickname)
    store.userStore?.setAddress(
      enteredAddress,
      typedCompleteAddress || toJS(store?.userStore?.completeAddress)
    );
    setEnableContinue(false);

    // Update URL and close immediately
    router.replace(
      {
        pathname: router?.pathname,
        query: {
          ...QueryString.parse(router?.query),
          address: enteredAddress,
        },
      },
      undefined,
      { shallow: true }
    );
    closeModal();
  };

  const buildFullAddress = attributes => {
    if (!attributes) return '';
    const suitePart = attributes?.suite ? `${attributes.suite} - ` : '';
    const streetPart = `${attributes?.street_number ?? ''} ${attributes?.street_name ?? ''}`.trim();
    const cityProvPostal = `${attributes?.city ?? ''}, ${attributes?.province ?? ''} ${
      attributes?.postal_code ?? ''
    }`.trim();
    return `${suitePart}${streetPart}, ${cityProvPostal}`
      .replace(/\s+,/g, ',')
      .replace(/,\s*,/g, ', ');
  };

  const AddressItem = ({ addressObj, onSelect }) => {
    const nickname = addressObj?.attributes?.nickname || 'Address';
    const destinationCode = addressObj?.attributes?.destination_code;
    return (
      <button
        type="button"
        className="flex items-center justify-between w-full py-4"
        onClick={() => onSelect(addressObj)}>
        <div className="flex items-center gap-3">
          <span className="flex items-center justify-center w-6 h-6">
            <Image src={MapIcon} alt="Map Icon" width={20} height={20} />
          </span>
          <div className="flex flex-col items-start text-left">
            <span className="text-[var(--Text-Paragraph,#2D3958)] font-inter text-[16px] font-normal leading-[24px]">
              {nickname}
              {destinationCode ? ` (${destinationCode})` : ''}
            </span>
            <span className="text-[var(--Text-Secondary-text,#566985)] font-inter text-[14px] font-normal leading-[21px]">
              {buildFullAddress(addressObj?.attributes)}
            </span>
          </div>
        </div>
        <Image src={ArrowRightIcon} alt="Select" width={16} height={16} />
      </button>
    );
  };

  const submitButtons = (
    <div className="flex flex-col">
      <button
        type="submit"
        className={classNames({
          'flex h-12 min-w-[98px] px-4 py-2 justify-center items-center gap-2 self-stretch rounded-full bg-[#575BE8] text-white': true,
          'bg-gray-400': enableContinue,
          'hover:bg-gray-400': enableContinue,
        })}
        disabled={enableContinue}>
        Continue with this address
      </button>
    </div>
  );

  return (
    <Transition appear show={visible} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={() => {
          setModal(false);
        }}>
        <form onSubmit={onContinuePress}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed top-0 right-0 left-0 sm:inset-0 overflow-y-auto">
            <div className="flex min-h-screen sm:min-h-full sm:items-center sm:justify-center sm:p-4 md:p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95">
                <Dialog.Panel className="flex flex-col w-full transform overflow-hidden bg-white text-left align-middle shadow-xl transition-all h-screen min-h-screen px-4 pt-8 pb-16 sm:h-auto sm:min-h-0 sm:w-[480px] sm:rounded-2xl sm:py-10 sm:px-8">
                  {/* Mobile layout */}
                  <div className="block sm:hidden w-full">
                    {/* Title and description */}
                    <Dialog.Title
                      as="h3"
                      className="text-[var(--plain,#2D3958)] font-inter font-semibold text-[26px] leading-[39px]">
                      Confirm Delivery Address
                    </Dialog.Title>
                    <p className="mt-2 text-[var(--plain,#2D3958)] font-inter text-[16px] font-normal leading-[24px]">
                      The address you entered matches multiple addresses in your account. Please
                      select the correct one to proceed.
                    </p>

                    <div className="mt-6 flex flex-col items-start self-stretch">
                      {/* Entered Address */}
                      <div className="flex flex-col items-start gap-1 self-stretch">
                        <span className="text-[var(--Text-Secondary-text,#566985)] font-inter text-[16px] font-medium leading-[24px]">
                          Entered Address
                        </span>
                        <div className="flex items-center justify-between w-full rounded-lg border border-[#F4F4F4] bg-[#F4F4F4] px-3 py-3">
                          <div className="flex items-center gap-2 w-full">
                            <Image src={MapIcon} alt="Map Icon" width={20} height={20} />
                            <span className="flex-1 truncate text-[var(--Text-Secondary-text,#566985)] font-inter text-[16px] font-normal leading-[24px]">
                              {enteredAddress}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Use as New Address */}
                      <div className="flex h-11 p-2 justify-end items-center gap-2 self-stretch">
                        <button
                          type="button"
                          className="flex items-center gap-2"
                          onClick={selectNewAddress}>
                          <span className="text-[var(--Primary-MP,#575BE8)] font-inter text-[16px] font-normal leading-[24px]">
                            Use as New Address
                          </span>
                          <Image
                            src={ArrowRightIcon}
                            alt="Use as New Address"
                            width={16}
                            height={16}
                          />
                        </button>
                      </div>

                      <div className="w-full border-t" style={{ borderColor: '#DEE2E6' }} />

                      {/* Duplicate addresses list */}
                      <div
                        className="flex flex-col self-stretch divide-y"
                        style={{ borderColor: '#DEE2E6' }}>
                        {Array.isArray(addresses) &&
                          addresses.map(ad => (
                            <AddressItem
                              key={ad?.id}
                              addressObj={ad}
                              onSelect={selectExistingAddress}
                            />
                          ))}
                      </div>
                    </div>
                  </div>

                  {/* Desktop/Tablet layout */}
                  <div className="hidden sm:block">
                    <div className="flex w-full justify-start items-start">
                      <Dialog.Title
                        as="h3"
                        style={{
                          fontSize: screenType?.isTabletOrMobile ? 23 : 32,
                        }}
                        className="font-inter-semibold leading-6 text-black-light">
                        <span className="text-[24px]">Confirm Delivery Address </span>
                      </Dialog.Title>
                    </div>

                    <div className="flex flex-col justify-center items-center flex-wrap mt-4 md:w-full">
                      <p
                        id="dialog-description"
                        className="text-[var(--plain,#2D3958)] font-inter text-base font-normal leading-6 text-left mb-8 self-stretch">
                        The address you entered matches multiple addresses in your account. Please
                        select the correct one to proceed.
                      </p>
                      <div className="flex flex-col w-full divide-y divide-border-gray">
                        {Array.isArray(addresses) &&
                          addresses?.map(adress => {
                            const formattedAddress = formattedAddres(adress?.attributes);

                            return (
                              <div
                                className="flex justify-between text-md font-inter-regular rounded bg-white w-full overflow-hidden focus:outline-none h-[72px] mt-3"
                                key={adress?.id}>
                                <label className="inline-flex items-center py-2 text-sm select-none mr-4 gap-2 px-2">
                                  <input
                                    name="addressChoice"
                                    autoComplete="off"
                                    type="radio"
                                    className="form-checkbox"
                                    checked={
                                      !isNewAddressSelected && selectedAddressId === adress.id
                                    }
                                    onChange={() => selectExistingAddress(adress)}
                                  />
                                  <span className="text-base text-slate-700 leading-7 font-inter-medium">
                                    {formattedAddress}
                                  </span>
                                </label>
                              </div>
                            );
                          })}

                        <div className="flex justify-between text-md font-inter-regular rounded bg-white w-full overflow-hidden focus:outline-none h-[72px] mt-3 mb-3">
                          <label className="inline-flex items-center py-2 text-sm select-none mr-4 gap-2 px-2">
                            <input
                              name="addressChoice"
                              autoComplete="off"
                              type="radio"
                              className="form-checkbox"
                              checked={isNewAddressSelected}
                              onChange={selectNewAddress}
                            />
                            <span className="text-base text-slate-700 leading-7 font-inter-medium">
                              Add as new address
                            </span>
                          </label>
                        </div>

                        {error && (
                          <span className="mt-5 text-red-600 text-md font-inter-medium">
                            {error}
                          </span>
                        )}

                        <div className="hidden sm:flex flex-col">{submitButtons}</div>
                      </div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </form>
      </Dialog>
    </Transition>
  );
}

export default observer(DuplicateAddressConfirmation);
